import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFeedbackPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, in_progress, resolved, closed
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [response, setResponse] = useState('');

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchFeedbacks();
    } else if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredFeedbacks(feedbacks);
    } else {
      setFilteredFeedbacks(feedbacks.filter(fb => fb.status === filter));
    }
  }, [filter, feedbacks]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/feedback/admin/all`, {
        withCredentials: true
      });
      setFeedbacks(response.data.feedbacks || []);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId, newStatus) => {
    try {
      await axios.post(
        `${API}/feedback/admin/${feedbackId}/status?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      fetchFeedbacks();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const submitResponse = async (feedbackId) => {
    if (!response.trim()) return;

    try {
      await axios.post(
        `${API}/feedback/admin/${feedbackId}/respond?response_text=${encodeURIComponent(response)}`,
        {},
        { withCredentials: true }
      );
      setResponse('');
      setSelectedFeedback(null);
      fetchFeedbacks();
      alert(language === 'tr' ? '✅ Yanıt gönderildi!' : '✅ Response sent!');
    } catch (error) {
      console.error('Error submitting response:', error);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm(language === 'tr' ? 'Silmek istediğinizden emin misiniz?' : 'Are you sure?')) return;

    try {
      await axios.delete(`${API}/feedback/admin/${feedbackId}`, {
        withCredentials: true
      });
      fetchFeedbacks();
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'bug': '🐛',
      'feature': '✨',
      'improvement': '🔧',
      'question': '❓',
      'other': '💭'
    };
    return icons[type] || '💬';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Geri Bildirim Yönetimi' : 'Feedback Management'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            💬 {language === 'tr' ? 'Geri Bildirim Yönetimi' : 'Feedback Management'}
          </h1>
          <p className="text-gray-600">
            {language === 'tr' ? 'Kullanıcı geri bildirimlerini görüntüle ve yönet' : 'View and manage user feedbacks'}
          </p>
        </div>

        {/* Stats & Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { key: 'all', label: language === 'tr' ? 'Tümü' : 'All', count: feedbacks.length, color: 'bg-gray-100' },
            { key: 'new', label: language === 'tr' ? 'Yeni' : 'New', count: feedbacks.filter(f => f.status === 'new').length, color: 'bg-blue-100' },
            { key: 'in_progress', label: language === 'tr' ? 'İşlemde' : 'In Progress', count: feedbacks.filter(f => f.status === 'in_progress').length, color: 'bg-yellow-100' },
            { key: 'resolved', label: language === 'tr' ? 'Çözüldü' : 'Resolved', count: feedbacks.filter(f => f.status === 'resolved').length, color: 'bg-green-100' },
            { key: 'closed', label: language === 'tr' ? 'Kapalı' : 'Closed', count: feedbacks.filter(f => f.status === 'closed').length, color: 'bg-gray-100' }
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={`${item.color} rounded-xl p-4 transition-all ${filter === item.key ? 'ring-2 ring-blue-600 shadow-lg' : 'hover:shadow-md'}`}
            >
              <div className="text-2xl font-bold text-gray-800">{item.count}</div>
              <div className="text-sm text-gray-600">{item.label}</div>
            </button>
          ))}
        </div>

        {/* Feedbacks List */}
        <div className="space-y-4">
          {filteredFeedbacks.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center text-gray-500">
              {language === 'tr' ? 'Geri bildirim bulunamadı' : 'No feedbacks found'}
            </div>
          ) : (
            filteredFeedbacks.map((fb) => (
              <div key={fb.id} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getTypeIcon(fb.type)}</span>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{fb.title}</h3>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStatusColor(fb.status)}`}>
                          {fb.status}
                        </span>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getPriorityColor(fb.priority)}`}>
                          {fb.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={fb.status}
                      onChange={(e) => updateStatus(fb.id, e.target.value)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="new">New</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    <button
                      onClick={() => deleteFeedback(fb.id)}
                      className="text-red-600 hover:text-red-800 px-2"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-gray-700">{fb.description}</p>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-600 mb-4 pb-4 border-b">
                  <div>
                    👤 {fb.user_name} ({fb.user_email}) - {fb.user_role}
                  </div>
                  <div>
                    📅 {new Date(fb.created_at).toLocaleString()}
                  </div>
                </div>

                {/* Responses */}
                {fb.responses && fb.responses.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-800 mb-3">
                      {language === 'tr' ? 'Yanıtlar:' : 'Responses:'}
                    </h4>
                    {fb.responses.map((resp, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 mb-2">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-sm text-blue-600">{resp.admin_name}</span>
                          <span className="text-xs text-gray-500">{new Date(resp.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-700">{resp.response_text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Response Form */}
                {selectedFeedback === fb.id ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder={language === 'tr' ? 'Yanıtınız...' : 'Your response...'}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitResponse(fb.id)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                      >
                        {language === 'tr' ? 'Gönder' : 'Send'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedFeedback(null);
                          setResponse('');
                        }}
                        className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                      >
                        {language === 'tr' ? 'İptal' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setSelectedFeedback(fb.id)}
                    className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                  >
                    💬 {language === 'tr' ? 'Yanıtla' : 'Respond'}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminFeedbackPage;
